import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectAlgoExpiryDate } from '@/store/orders/algoOrders/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const AlgoExpiryDate = () => {
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, formatTime, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const expiryDate = useAppSelector((state) => selectAlgoExpiryDate(state, tileId));

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleGoodTillDateChange = (goodTillDate: boolean) => {
    track('Order action', 'Good till date changed');
    validateAlgo({
      expiryDate: goodTillDate
        ? { goodTillDate: true, date: expiryDate?.date, time: expiryDate?.time }
        : { goodTillDate: false, date: expiryDate?.date, time: expiryDate?.time },
    });
  };

  const handleDateChange = (date: string) => {
    track('Order action', 'Expiry date changed');
    if (isNotDefined(expiryDate) || expiryDate.goodTillDate) {
      return;
    }

    validateAlgo({
      expiryDate: {
        goodTillDate: false,
        date,
        time: expiryDate?.time,
      },
    });
  };

  const handleTimeChange = ({
    hour = 0,
    minute = 0,
    second = 0,
    period,
  }: {
    hour?: number;
    minute?: number;
    second?: number;
    period?: 'AM' | 'PM';
  }) => {
    track('Order action', 'Expiry time changed');
    if (isNotDefined(expiryDate) || expiryDate.goodTillDate) {
      return;
    }

    const hour24 = period === 'PM' && hour < 12 ? hour + 12 : hour;

    if (isDefined(expiryDate.date)) {
      const newTime = formatTime(new Date(0, 0, 0, hour24, minute, second), { timeStyle: 'medium', hour12: false });
      const date = format(expiryDate.date, 'yyyy-MM-dd');

      validateAlgo({
        expiryDate: {
          goodTillDate: false,
          date: date,
          time: newTime,
        },
      });
    }
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const expiryDateDateError = errors?.expiryDate;

  return (
    <OrderRow labelId="order.fields.expiryDate.label" alignTop>
      <Checkbox
        dataE2e="GTC-checkbox"
        checked={expiryDate?.goodTillDate}
        onCheckBoxChange={handleGoodTillDateChange}
        label={formatMessage({ id: 'order.fields.expiryDate.goodTillDate' })}
        size="sm"
      />
      <div
        className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
        data-e2e="expiry-date"
      >
        <OmsFieldErrorWrapper errors={expiryDateDateError}>
          <SgDatePicker
            size="sm"
            yearControls
            value={expiryDate?.date}
            placeholder="Expiry date"
            disabled={expiryDate?.goodTillDate}
            locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
            dateInputCultureInfo={getComputedLocale()}
            onChange={handleDateChange}
            onFocus={() => setIsModifyingField(true)}
            onBlurInput={() => setIsModifyingField(false)}
            showClearButton={false}
            validationState={isDefined(expiryDateDateError) ? 'invalid' : undefined}
            dateFormat={DATE_FORMAT_TEXT}
          />
        </OmsFieldErrorWrapper>

        <div data-e2e="expiry-time-input">
          <SgTimepicker
            size="sm"
            appendTo="body"
            withSeconds
            onTimeSelected={(e) => handleTimeChange(e.detail)}
            value={expiryDate?.time}
            disabled={expiryDate?.goodTillDate}
          />
        </div>
      </div>
    </OrderRow>
  );
};
